import {
  UseMutationOptions,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { forceHideSidebar } from '@components/sidebar';
import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { getJourneyQueryKey, useInvalidateJourneys } from 'api/journeys/hooks';
import { handleError, handlePossibleNonFieldError } from 'api/query-client';
import {
  BASE_ACTION_TYPE,
  CreateJourneyActionData,
  JourneyAction,
  JourneyActionDetailed,
  JourneyType,
} from 'api/types';
import { isInTemplateRoute } from 'ui/router';

import {
  JourneyActionMoves,
  createJourneyAction,
  createJourneyActionRelationship,
  duplicateJourneyAction,
  listJourneyActionDependants,
  listJourneyActionRelationships,
  loadJourneyAction,
  loadJourneyActionAvailableMoves,
  loadJourneyActionIsInsideLoop,
  loadJourneyActions,
  loadJourneyTemplateActions,
  moveJourneyAction,
  removeJourneyAction,
  removeJourneyActionRelationship,
  updateJourneyAction,
  updateJourneyActionDescription,
} from './requests';

const getJourneyActionsQueryKey = (shortId?: JourneyType['shortId']) =>
  ['journey', shortId, 'action'].filter(Boolean);

export const useJourneyActions = (
  journeyId: string,
  options?: { onSuccess?: (data: JourneyAction[]) => void; enabled?: boolean }
) => {
  const loadFunction = isInTemplateRoute() ? loadJourneyTemplateActions : loadJourneyActions;
  return useQuery({
    queryKey: getJourneyActionsQueryKey(journeyId),
    queryFn: () => loadFunction(journeyId),
    enabled: Boolean(journeyId),
    ...options,
  });
};

export const getJourneyActionQueryKey = (shortId?: JourneyAction['shortId']) =>
  ['journey', 'action', shortId].filter(Boolean);

export const useJourneyAction = <ActionType extends BASE_ACTION_TYPE>(
  shortId: string,
  options?: { onSuccess?: (data: JourneyActionDetailed<ActionType>) => void }
) => {
  return useQuery({
    queryKey: getJourneyActionQueryKey(shortId),
    queryFn: () => loadJourneyAction<ActionType>(shortId),
    ...options,
  });
};

export const useJourneyActionIsInsideLoop = (shortId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...getJourneyActionQueryKey(shortId), 'is-inside-loop'],
    queryFn: () => loadJourneyActionIsInsideLoop(shortId),
    ...options,
  });
};

export const useInvalidateJourneyActions = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyAction = (shortId?: JourneyAction['shortId']) =>
    queryClient.invalidateQueries(getJourneyActionQueryKey(shortId));
  const invalidateJourneyActions = (shortId?: JourneyType['shortId']) =>
    queryClient.invalidateQueries(getJourneyActionsQueryKey(shortId));
  const removeJourneyActionQuery = (shortId?: JourneyAction['shortId']) => {
    queryClient.removeQueries(getJourneyActionQueryKey(shortId));
  };

  return { invalidateJourneyAction, invalidateJourneyActions, removeJourneyActionQuery };
};

export const useCreateJourneyAction = (
  options?: UseMutationOptions<{ shortId: string }, any, CreateJourneyActionData>
) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    ...options,
    mutationFn: (data) => createJourneyAction(data),
    onSuccess: (_, vars) => {
      invalidateJourney(vars.journey);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('journeyActionNotSaved'));
    },
  });
};

export const useUpdateJourneyAction = (
  shortId: string,
  options?: UseMutationOptions<JourneyActionDetailed, any, object>
) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyAction } = useInvalidateJourneyActions();
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    ...options,
    mutationFn: (data) => updateJourneyAction(shortId, data),
    onSuccess: async (...data) => {
      !options?.onSuccess && forceHideSidebar();
      options?.onSuccess?.(...data);
      await Promise.all([
        invalidateJourney(data[0].journey),
        invalidateJourneyAction(shortId),
        queryClient.invalidateQueries(['journey-integration-endpoints', data[0].journey]),
      ]);
    },
    onError: (...data) => {
      if (options?.onError) {
        options?.onError?.(...data);
      } else {
        handlePossibleNonFieldError(data, t('journeyActionNotSaved'));
      }
    },
  });
};

export const useUpdateJourneyActionDescription = (
  shortId: string,
  journeyShortId: string,
  options?: UseMutationOptions<void, any, string>
) => {
  const client = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data) => updateJourneyActionDescription(shortId, data),
    onSuccess: (...vars) => {
      client.invalidateQueries(getJourneyQueryKey(journeyShortId), { exact: true });
      client.invalidateQueries(getJourneyActionsQueryKey(journeyShortId));
      options?.onSuccess?.(...vars);
    },
  });
};

export const useRemoveJourneyAction = (options?: UseMutationOptions<unknown, any, string>) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const client = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (shortId) => removeJourneyAction(shortId),
    onSuccess: (data, variables, context) => {
      client.removeQueries(getJourneyActionQueryKey(variables));
      invalidateJourney();
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useJourneyActionRelationships = (shortId: string) => {
  return useQuery({
    queryKey: ['journey', shortId, 'actions', 'relationships'],
    queryFn: () => listJourneyActionRelationships(shortId),
  });
};

export const useJourneyActionDependants = (
  shortId: JourneyAction['shortId'],
  options?: Partial<{ enabled: boolean }>
) => {
  return useQuery({
    queryKey: ['journey', shortId, 'actions', 'dependants'],
    queryFn: () => listJourneyActionDependants(shortId),
    ...options,
  });
};

export const useCreateJourneyActionRelationship = (
  options?: UseMutationOptions<JourneyAction, any, { parentShortId: string; childShortId: string }>
) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    ...options,
    mutationFn: (data) =>
      createJourneyActionRelationship({ parent: data.parentShortId, child: data.childShortId }),
    onSuccess: (...data) => {
      invalidateJourney();
      options?.onSuccess?.(...data);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('journeyActionNotSaved'));
    },
  });
};

export const useRemoveJourneyActionRelationship = (
  options?: UseMutationOptions<unknown, any, string>
) => {
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    ...options,
    mutationFn: (journeyId) => removeJourneyActionRelationship(journeyId),
    onSuccess: (...data) => {
      invalidateJourney();
      options?.onSuccess?.(...data);
    },
  });
};

export const locale = {
  en: {
    journeyActionNotSaved: 'Journey action not saved',
    journeyActionNotDuplicated: 'Action not duplicated',
    journeyActionDuplicated: 'Action duplicated successfully',
  },
};

export const useJourneyActionAvailableMoves = (
  shortId: string,
  options?: UseQueryOptions<unknown, any, JourneyActionMoves>
) => {
  return useQuery({
    queryKey: ['journey', shortId, 'actions', 'moves'],
    queryFn: () => loadJourneyActionAvailableMoves(shortId),
    ...options,
  });
};

export const useMoveJourneyAction = () => {
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: moveJourneyAction,
    onSuccess: (_, requestData) => {
      invalidateJourney(requestData.journeyId);
    },
  });
};

export const useDuplicateJourneyAction = (shortId: string) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: () => duplicateJourneyAction(shortId),
    onSuccess: () => {
      invalidateJourney();
      showSuccess(t('journeyActionDuplicated'));
    },
    onError: () => {
      handleError(t('journeyActionNotDuplicated'));
    },
  });
};
